import React, { useState } from 'react'
import moment from 'moment-timezone'
import TableCell from '@material-ui/core/TableCell'
import classNames from 'classnames'
import { Typography, Link, VaultCasesBulkDownloadButton, Avatar, Tooltip, ProviderPopover } from 'components'
import { CONSTANTS, colors } from 'constants/index'
import { useStyles } from './rowStyles'
import { useSelector } from 'react-redux'
import { ReactComponent as SecureIcon } from 'static/vault/secure_folder.svg'
import { ReactComponent as MainFolderIcon } from 'static/vault/casesList/folder_row.svg'
import { ReactComponent as FolderActiveIcon } from 'static/vault/casesList/folder_active.svg'
import { ReactComponent as FolderInactiveIcon } from 'static/vault/casesList/folder_inactive.svg'
import { ReactComponent as FileActiveIcon } from 'static/vault/casesList/file_active.svg'
import { ReactComponent as FileInactiveIcon } from 'static/vault/casesList/file_inactive.svg'
import { ReactComponent as ShareIcon } from 'static/cases/share.svg'
import { formatBytes } from 'utils/vault/vault-helpers'
import PeopleWithAccessModal from '../../cases/PeopleWithAccessModal'

const FolderInfo = ({ folderAmount, classes }) => {
  return (
    <>
      {folderAmount ? (
        <FolderActiveIcon className={classes.secondaryIcon} />
      ) : (
        <FolderInactiveIcon className={classes.secondaryIcon} />
      )}
      <Typography className={classNames(classes.numbers, { [classes.inactiveNumber]: !folderAmount })}>
        {folderAmount}
      </Typography>
    </>
  )
}

const FileInfo = ({ fileAmount, classes }) => {
  return (
    <div className={classes.fileInfoContainer}>
      {fileAmount ? (
        <FileActiveIcon className={classes.secondaryIcon} />
      ) : (
        <FileInactiveIcon className={classes.secondaryIcon} />
      )}
      <Typography className={classNames(classes.numbers, { [classes.inactiveNumber]: !fileAmount })}>
        {fileAmount}
      </Typography>
    </div>
  )
}

const getInitials = (firstName, lastName) => {
  const first = firstName ? firstName.charAt(0).toUpperCase() : ''
  const last = lastName ? lastName.charAt(0).toUpperCase() : ''
  return first + last || '?'
}

const ProvidersAvatars = ({ providers, classes, onAvatarClick, onLastAvatarClick }) => {
  if (!providers || providers.length === 0) {
    return null
  }

  const visibleProviders = providers.slice(0, 3)
  const remainingCount = providers.length - 3

  const handleMouseDown = e => {
    e.stopPropagation()
    e.preventDefault()
    e.nativeEvent.stopImmediatePropagation()
  }

  const handleAvatarClick = (provider, e, isLast) => {
    e.stopPropagation()
    e.preventDefault()
    e.nativeEvent.stopImmediatePropagation()
    if (isLast && onLastAvatarClick) {
      onLastAvatarClick()
    } else if (!isLast && onAvatarClick) {
      onAvatarClick(provider, e)
    }
  }

  return (
    <div data-provider-avatars className={classes.providersContainer} onMouseDown={handleMouseDown}>
      {visibleProviders.map((provider, index) => {
        const initials = getInitials(provider.first_name, provider.last_name)
        const isLast = index === visibleProviders.length - 1 && remainingCount > 0
        const zIndex = providers.length - index

        return (
          <div
            key={provider.username || index}
            className={classes.providerAvatarWrapper}
            style={{ zIndex, marginLeft: index > 0 ? -8 : 0 }}
            onClick={e => handleAvatarClick(provider, e, isLast)}
          >
            <Avatar className={classes.providerAvatar} src={provider.photo_thumbnail || provider.photo || null}>
              {!provider.photo_thumbnail && !provider.photo && initials}
            </Avatar>
            {isLast && (
              <div className={classes.providerAvatarOverlay}>
                <Typography className={classes.providerAvatarCount}>+{remainingCount}</Typography>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

const VaultCasesTableRow = ({ data, timezone, isAccountant }) => {
  const classes = useStyles()
  const currentUser = useSelector(state => state.auth.currentUser)
  const isClient = currentUser?.role === 'client'
  const [showPeopleModal, setShowPeopleModal] = useState(false)
  const [providerPopoverAnchor, setProviderPopoverAnchor] = useState(null)
  const [selectedProvider, setSelectedProvider] = useState(null)

  const isSharedByMe = data.isCaseFolder && data.sharedWithCount > 0
  const isSharedWithMe = data.isCaseFolder && data.sharedBy && data.sharedByName

  const getShareTooltip = () => {
    if (isSharedByMe) {
      return `Project shared by you with ${data.sharedWithCount} provider(s)`
    } else if (isSharedWithMe && data.sharedByName) {
      return `This client's projects shared with you by ${data.sharedByName}`
    }
    return ''
  }

  const handleShareIconClick = e => {
    e.stopPropagation()
    e.preventDefault()
    e.nativeEvent.stopImmediatePropagation()
    setShowPeopleModal(true)
  }

  const handleShareIconMouseDown = e => {
    e.stopPropagation()
    e.preventDefault()
    e.nativeEvent.stopImmediatePropagation()
  }

  const handleProviderAvatarClick = (provider, e) => {
    e.stopPropagation()
    e.preventDefault()
    e.nativeEvent.stopImmediatePropagation()
    setSelectedProvider(provider)
    setProviderPopoverAnchor(e.currentTarget)
  }

  const handleProviderPopoverClose = e => {
    e.stopPropagation()
    e.preventDefault()
    setProviderPopoverAnchor(null)
    setSelectedProvider(null)
  }

  const handleLastAvatarClick = () => {
    setShowPeopleModal(true)
  }

  return (
    <>
      <TableCell classes={{ body: classes.tableCell }}>
        <div className={classes.cell}>
          {data.isGeneralFolder || data.isPrivateFolder ? (
            <SecureIcon className={classes.mainFolderIcon} />
          ) : (
            <MainFolderIcon className={classes.mainFolderIcon} />
          )}
          <Link className={classes.link}>{data.name}</Link>
          {!isClient && (isSharedByMe || isSharedWithMe) && (
            <Tooltip tooltipClass={classes.tooltip} title={getShareTooltip()} placement="bottom-start">
              <div data-share-icon onClick={handleShareIconClick} onMouseDown={handleShareIconMouseDown}>
                <ShareIcon className={`${classes.shareButton} ${isSharedWithMe ? classes.shareButtonRotated : ''}`} />
              </div>
            </Tooltip>
          )}
        </div>
      </TableCell>
      {(isAccountant || isClient) && (
        <TableCell classes={{ body: classes.tableCell }}>
          <ProvidersAvatars
            providers={data.providers}
            classes={classes}
            onAvatarClick={handleProviderAvatarClick}
            onLastAvatarClick={handleLastAvatarClick}
          />
          {selectedProvider && (
            <ProviderPopover
              provider={selectedProvider}
              anchorEl={providerPopoverAnchor}
              open={Boolean(providerPopoverAnchor)}
              onClose={handleProviderPopoverClose}
            />
          )}
        </TableCell>
      )}
      <TableCell classes={{ body: classes.tableCell }}>{`${formatBytes(data.size)}`}</TableCell>
      <TableCell classes={{ body: classes.tableCell }}>
        <div className={classes.cell}>
          <FolderInfo folderAmount={data.folderCount} classes={classes} />
          <FileInfo fileAmount={data.fileCount} classes={classes} />
        </div>
      </TableCell>
      <TableCell classes={{ body: classes.tableCell }}>
        {data.lastUploaded
          ? moment(data.lastUploaded)
              .tz(timezone)
              .format(CONSTANTS.DATE_FORMAT.FORMAT_ONE)
          : '-'}
      </TableCell>
      <TableCell classes={{ body: classes.tableCell }}>
        <VaultCasesBulkDownloadButton disabled={!data.folderCount && !data.fileCount} data={data} />
      </TableCell>
      <PeopleWithAccessModal
        isOpen={showPeopleModal}
        onClose={() => setShowPeopleModal(false)}
        providers={data.providers || []}
      />
    </>
  )
}

export default VaultCasesTableRow
